import { useAsyncOperation } from "@core/hooks/use-async-operation";
import { useOnceEffect } from "@core/hooks/use-once-effect";
import { useI18n } from "@core/i18n/i18n";
import { useNavigate } from "@core/routing/use-navigate";
import { patientManager } from "@core/service-container";
import { Patient, PatientNameListItem, PendingPatient } from "@domain/patient/patient";
import { PendingPatientForbiddenAccessError } from "@domain/patient/patient-errors";
import { RoutesPath } from "@ui/navigation/routes";
import { HomeTabNames } from "@ui/pages/home/components/patient-tabs";
import { isNotNil, isNotEmpty } from "@utils/assert";
import { formatName } from "@utils/string-utils";
import { useComputedObservable } from "micro-observables";
import { useCallback, useEffect, useState } from "react";

type PatientNameDetails = { firstName: string; lastName: string; activationCode?: string };

interface UseFormatPatientsHook {
	formatPatientName: (
		patientNameDetails: PatientNameDetails,
		fallbackToActivationCode?: boolean,
		fallbackToNamePlaceholder?: boolean,
	) => string;
}

export const useFormatPatients = (): UseFormatPatientsHook => {
	const { format } = useI18n();
	const formatPatientName = useCallback(
		(
			{ firstName, lastName, activationCode }: PatientNameDetails,
			fallbackToActivationCode = true,
			fallbackToNamePlaceholder = true,
		): string => {
			if (isNotEmpty(firstName) && isNotEmpty(lastName)) {
				return formatName({ firstName, lastName });
			}
			if (fallbackToActivationCode && isNotNil(activationCode)) {
				return activationCode;
			}
			if (fallbackToNamePlaceholder) {
				return format("home.registeredPatientsListing.emptyName");
			}
			return "";
		},
		[format],
	);

	return { formatPatientName };
};

interface UsePatientsHook {
	patient: Patient | PendingPatient | undefined;
	loading: boolean;
	error: unknown;
	refreshPatient: (patientId: string) => Promise<void>;
}

export function usePatient(patientId?: string, withFetch = true): UsePatientsHook {
	const [loading, setLoading] = useState(withFetch);
	const [error, setError] = useState<unknown>(undefined);
	const navigate = useNavigate();

	const patient = useComputedObservable(
		() => (patientId ? patientManager.patientMap.get().get(patientId) : undefined),
		[patientId],
	);

	const refreshPatient = useCallback(
		async (patientIdToBeRefreshed: string) => {
			try {
				await patientManager.retrievePatient(patientIdToBeRefreshed);
				setError(null);
			} catch (e) {
				if (e instanceof PendingPatientForbiddenAccessError) {
					navigate(RoutesPath.Home, { query: { active: HomeTabNames.Pending } });
				} else {
					setError(e);
					console.error("Failed to retrieve the patient", e);
				}
			} finally {
				setLoading(false);
			}
		},
		[navigate],
	);

	useEffect(() => {
		withFetch && patientId && refreshPatient(patientId);
	}, [patientId, refreshPatient, withFetch]);

	return { loading, patient, error, refreshPatient };
}

export function usePatientNames(): {
	patientNames: PatientNameListItem[];
	loading: boolean;
} {
	const [patientNames, setPatientNames] = useState<PatientNameListItem[]>([]);

	const [fetchRegisteredPatientNames, loading] = useAsyncOperation(async () => {
		const result = await patientManager.listRegisteredPatientNames();
		setPatientNames(result);
	}, {});

	useOnceEffect(async () => await fetchRegisteredPatientNames());

	return { loading, patientNames };
}
